'use client';

import { Children, isValidElement, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import { HorizontalSeparator } from '@/app_components/ui/Separator';
import { cn } from '@/app_lib/utils/utils';

function getKey(item: ReactNode, index: number) {
  if (typeof item === 'object' && item !== null && 'key' in item && item.key != null) {
    return String(item.key);
  }
  return `step-${index}`;
}

function getDataTitle(item: ReactNode): string {
  if (
    isValidElement(item) &&
    item.props != null &&
    typeof item.props === 'object' &&
    'data-title' in item.props &&
    item.props['data-title'] != null
  ) {
    return String(item.props['data-title']);
  }
  return '';
}

export interface StepCarouselProps {
  children?: ReactNode;
  className?: string;
  /** Auto-advance interval in ms. Omit or 0 to disable. */
  autoAdvanceMs?: number;
}

export const StepCarousel = ({ children, className, autoAdvanceMs = 0 }: StepCarouselProps) => {
  const items = useMemo(() => Children.toArray(children), [children]);
  const count = items.length;
  const [activeIndex, setActiveIndex] = useState(0);

  const stepTo = useCallback(
    (index: number) => {
      setActiveIndex(() => {
        if (index < 0) {
          return count - 1;
        }
        if (index >= count) {
          return 0;
        }
        return index;
      });
    },
    [count],
  );

  const stepNext = useCallback(() => stepTo(activeIndex + 1), [stepTo, activeIndex]);
  const stepPrev = useCallback(() => stepTo(activeIndex - 1), [stepTo, activeIndex]);

  useEffect(() => {
    if (!autoAdvanceMs || count <= 1) {
      return;
    }
    const id = setInterval(stepNext, autoAdvanceMs);
    return () => {
      clearInterval(id);
    };
  }, [autoAdvanceMs, count, stepNext]);

  const segmentWidthPercent = count > 0 ? 100 / count : 0;
  const currentLeftPercent = activeIndex * segmentWidthPercent;
  const titles = useMemo(() => items.map(getDataTitle), [items]);

  if (count === 0) {
    return null;
  }

  return (
    <div className={cn('flex flex-col layout-gap-md', className)}>
      <div className='relative overflow-hidden'>
        {items.map((item, index) => (
          <div
            aria-hidden={index !== activeIndex}
            className={cn(
              'w-full',
              index === activeIndex ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none',
            )}
            key={getKey(item, index)}
          >
            {item}
          </div>
        ))}
      </div>

      <div className={cn('flex flex-col gap-2', count < 2 && 'hidden', titles.some(Boolean) && 'h-[56px]')}>
        <div className='items-center gap-2 hidden lg:flex'>
          {/* Indicator */}
          <div className='flex-1 h-px min-w-0 relative'>
            <div className='absolute left-0 top-0 h-full bg-helper w-full' />
            <div
              className='hidden lg:block absolute top-0 h-full transition-[left] duration-300 ease-out bg-blue-1'
              style={{ left: `${currentLeftPercent}%`, width: `${segmentWidthPercent}%` }}
            />
            {/* Titles row - each segment width matches segmentWidthPercent */}
            {titles.some(Boolean) && (
              <div className='w-full h-[55px] text-center min-w-0 hidden lg:flex'>
                {titles.map((title, index) => (
                  <button
                    className={cn(
                      'block min-w-0 font-medium text-secondary py-xs w-full cursor-pointer',
                      index === activeIndex && 'text-blue-1 dark:text-blue-1',
                      index !== activeIndex && 'hidden lg:block max-lg:flex-[0_0_0]! max-lg:w-full!',
                    )}
                    key={getKey(items[index], index)}
                    onClick={() => stepTo(index)}
                    style={{ flex: `0 0 ${segmentWidthPercent}%` }}
                    type='button'
                  >
                    {title}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className='flex gap-md items-center justify-between w-full lg:hidden z-1'>
          <ActionableIcon
            aria-label='Previous'
            className='p-2 border rounded-full stroke-helper bg-secondary shrink-0 step-carousel-btn'
            onClick={() => stepPrev()}
          >
            <Icon icon='ChevronLeft' />
          </ActionableIcon>

          {titles.some(Boolean) && (
            <div className='flex w-full text-center min-w-0 lg:hidden'>
              {titles.map((title, index) => (
                <div
                  className={cn(
                    'flex gap-lg items-center font-medium text-secondary py-xs w-full transition-opacity duration-300 ease-out -mt-px',
                    index === activeIndex && 'text-blue-1 dark:text-blue-1 opacity-100',
                    index !== activeIndex && 'hidden w-full opacity-0',
                  )}
                  key={getKey(items[index], index)}
                >
                  <HorizontalSeparator className='stroke-secondary w-full' />
                  <span className='whitespace-nowrap'>{title}</span>
                  <HorizontalSeparator className='stroke-secondary w-full' />
                </div>
              ))}
            </div>
          )}

          <ActionableIcon
            aria-label='Next'
            className='p-2 border rounded-full stroke-helper bg-secondary shrink-0 step-carousel-btn'
            onClick={() => stepNext()}
          >
            <Icon icon='ChevronRight' />
          </ActionableIcon>
        </div>
      </div>
    </div>
  );
};
